import { motion } from "framer-motion";
import { Play, Youtube, Instagram, Mic } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Magnetic } from "@/components/animations/ScrollAnimations";
// Logo lives in /public/assets so the <link rel="preload"> in index.html
// matches the exact URL and the browser can discover it before React hydrates.
const logo = "/assets/logo-small.webp";
import heroPoster from "@/assets/hero-bg.jpg";

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster={heroPoster}
          aria-hidden="true"
          className="w-full h-full object-cover hero-video-drift"
        >
          <source src="https://storage.googleapis.com/msgsndr/KllvbZakLuJkDv2JgWhP/media/695c97caca807cc861605c77.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background/90" />
        <div className="absolute inset-0 bg-background/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center">
        <div className="max-w-5xl mx-auto">
          {/* Logo */}
          <img
            src={logo}
            alt="The Founders Lounge Podcast - Entrepreneur Podcast featuring real conversations with successful founders"
            width={431}
            height={144}
            fetchPriority="high"
            className="h-20 md:h-28 lg:h-36 w-auto mx-auto mb-2"
          />

          {/* H1 for SEO */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl text-foreground/90 font-light tracking-wide mb-4">
            <span className="sr-only">The Founders Lounge Podcast - </span>
            For Founders, By Founders
          </h1>

          {/* Subheadline */}
          <p className="text-sm md:text-base text-muted-foreground max-w-md mx-auto mb-14">
            <strong>Unfiltered founder stories.</strong> Real strategies from the people building what's next.
          </p>

          {/* CTAs */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Featured guest CTA, top of the hero on mobile where the menu is collapsed */}
            <Magnetic strength={0.2} className="w-full max-w-[21rem] sm:w-auto sm:max-w-none sm:hidden">
              <Button variant="hero" size="xl" className="w-full sm:w-auto animate-pulse-glow shadow-glow" asChild>
                <Link to="/apply#application-form" className="flex items-center gap-3">
                  <Mic className="w-5 h-5" />
                  <span>Be Our Guest</span>
                  <span className="inline-block animate-bounce-x">→</span>
                </Link>
              </Button>
            </Magnetic>

            <Magnetic strength={0.2} className="w-full max-w-[21rem] sm:w-auto sm:max-w-none">
              <Button variant="hero" size="xl" className="w-full sm:w-auto group" asChild>
                <a
                  href="https://www.youtube.com/@theFoundersLoungePodcast?sub_confirmation=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3"
                >
                  <Youtube className="w-6 h-6 transition-transform group-hover:scale-110" />
                  <span>Subscribe on YouTube</span>
                  <span className="inline-block animate-bounce-x">→</span>
                </a>
              </Button>
            </Magnetic>
            <Magnetic strength={0.2}>
              <Button variant="heroOutline" size="xl" className="group" asChild>
                <a
                  href="https://www.instagram.com/thefoundersloungepodcast/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3"
                >
                  <Instagram className="w-5 h-5 transition-transform group-hover:scale-110" />
                  Follow us on Instagram
                </a>
              </Button>
            </Magnetic>
          </motion.div>

          {/* Secondary action + trust line */}
          <motion.div
            className="mt-6 flex flex-col items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <a
              href="#latest-episodes"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
            >
              <Play className="w-4 h-4" />
              Watch the latest episode
              <span className="inline-block animate-bounce-x">→</span>
            </a>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              New founder interviews every week. Free. Forever.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator - CSS animation instead of framer-motion */}
      <button
        className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer animate-fade-in"
        style={{ animationDelay: '1.5s', animationFillMode: 'backwards' }}
        onClick={() => {
          window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
        }}
        aria-label="Scroll down"
      >
        <div className="w-8 h-14 rounded-full border-2 border-foreground/30 flex items-start justify-center p-2 hover:border-primary/50 transition-colors animate-scroll-bob">
          <div className="w-1.5 h-3 bg-primary rounded-full animate-scroll-dot" />
        </div>
      </button>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default HeroSection;
